import { Button, Menu, Pill, SortableCell, SortableHead, SortableList, formatRelative, type MenuItem, type SortableRows } from "@omnabase/ui";
import type { ChatOutsideRoom, ChatRoom } from "@omnabase/shared";
import { useSession } from "../../shell/store";
import { useParamNow, useSetParam, useWs } from "../../lib/nav";
import { useCan } from "../../lib/access";
import { orderChannels, patchChannel, useOutsideRooms } from "../../lib/chat";
import { mayManage, nameOf } from "../../lib/chatRooms";
import { useDroppedOrder } from "../../lib/order";
import { DeleteChannelDialog, EditChannelDialog, EditOutsideDialog, NewChannelDialog } from "./ChannelDialogs";

/** The channels screen (one management screen, the founder, 2026-10-12: "Make collections and channels work the same"): every channel the
 *  reader sees in the workspace's order, with its kind and its people; owners and administrators drag the open ones into the order everyone's
 *  column shows; Edit (the channel's dialog) and the row menu (Archive, Restore, Delete…) on what the reader manages, never on #general; New
 *  channel is the page's one button. A `team` holder also sees "Private channels you are not in" (2026-10-07), managed without being read.
 *  Its address: `/chat/manage/channels`, reached by the pencil beside the views column's Channels heading; the dialogs are
 *  `dialog=new-channel|edit-<id>|delete-<id>`. */
export function ChannelsManage({ rooms, reload }: { rooms: ChatRoom[]; reload: () => Promise<void> }) {
  const { workspace, me, toast } = useSession();
  const { slug } = useWs();
  const team = useCan()("team");
  const setParam = useSetParam();
  const asked = useParamNow("dialog"); // the dialogs open on the click itself
  const outside = useOutsideRooms(slug, team);
  const reloadAll = () => Promise.all([reload(), outside.reload()]).then(() => {});
  const channels = rooms.filter((r) => r.kind === "channel");
  const order = useDroppedOrder(channels.filter((c) => !c.archivedAt).map((c) => c.id));
  const open = order.ids.map((id) => channels.find((c) => c.id === id)).filter((c): c is ChatRoom => !!c);
  const archived = channels.filter((c) => c.archivedAt);
  const save = (next: string[]) => orderChannels(slug, next).then(() => reload());
  const onDrop = (from: number, to: number) => void order.drop(open.map((c) => c.id), from, to, save).catch((e: Error) => { toast(e.message, { kind: "error" }); void reload(); });
  const fail = (e: unknown) => toast((e as Error).message, { kind: "error" });
  const archive = (id: string, on: boolean) => void patchChannel(slug, id, { archived: on }).then(() => reloadAll(), fail);
  const active = workspace.members.filter((m) => m.status === "active").length;
  const people = (c: ChatRoom) => (c.private ? c.memberIds?.length ?? 0 : active);

  const row = (drag: SortableRows, c: ChatRoom, i: number | null) => {
    const may = mayManage(c, me.user.id, team);
    const items: MenuItem[] = !may ? [] : c.archivedAt
      ? [{ id: "restore", label: "Restore", icon: "refresh", onSelect: () => archive(c.id, false) }, { id: "delete", label: "Delete…", icon: "trash", onSelect: () => setParam("dialog", `delete-${c.id}`, true) }]
      : [{ id: "archive", label: "Archive", icon: "archive", onSelect: () => archive(c.id, true) }, { id: "delete", label: "Delete…", icon: "trash", onSelect: () => setParam("dialog", `delete-${c.id}`, true) }];
    return (
      <tr key={c.id} {...(i === null ? { className: "ob-dtable__row--dim" } : drag.rowProps(i))}>
        {team && <SortableCell>{i !== null && drag.handle(i, `#${c.slug}`)}</SortableCell>}
        <td className="ob-dtable__grow"><span className="ob-dtable__name" title={`#${c.slug}`}>#{c.slug}</span>{c.archivedAt && <> <Pill>Archived</Pill></>}{c.topic && <span className="ob-dtable__desc">{c.topic}</span>}</td>
        <td className="ob-dtable__col--phone">{c.private ? "Private" : "Public"}</td>
        <td className="ob-dtable__center">{" "}{people(c)}{" "}</td>{/* the spaces keep the number a word of its own in the row's text */}
        <td className="ob-dtable__actions"><span className="ob-dtable__row-actions">
          {may && !c.archivedAt && <Button size="sm" onClick={() => setParam("dialog", `edit-${c.id}`, true)}>Edit</Button>}
          {items.length > 0 && <Menu align="end" trigger={<Button variant="icon" size="sm" icon="more" aria-label={`More for #${c.slug}`} />} sections={[{ items }]} />}
        </span></td>
      </tr>
    );
  };
  return (
    <section className="ob-listpage" aria-label="Channels">
      <div className="ob-listpage__head">
        <h1 className="ob-listpage__title">Channels</h1>
        <span className="ob-listpage__count">{channels.length} {channels.length === 1 ? "channel" : "channels"}</span>
        <div className="ob-listpage__tools"><Button variant="primary" size="sm" icon="plus" onClick={() => setParam("dialog", "new-channel", true)}>New channel</Button></div>
      </div>
      <div className="ob-listpage__scroll"><div className="ob-manage">
        <p className="ob-manage__intro">{team ? "Everyone's column shows the channels in this order. Drag a row by its handle to move it." : "Everyone's column shows the channels in this order. You change the channels you made."}</p>
        <div className="ob-dtable-card">
          <SortableList ids={open.map((c) => c.id)} onDrop={onDrop}>
            {(drag) => (
              <table className="ob-dtable ob-dtable--manage" aria-label="Channels">
                <thead><tr>{team && <SortableHead />}<th>Name</th><th className="ob-dtable__col--phone">Kind</th><th className="ob-dtable__center">Members</th><th className="ob-dtable__actions"><span className="ob-sr-only">Actions</span></th></tr></thead>
                <tbody>
                  {open.map((c, i) => row(drag, c, i))}
                  {archived.map((c) => row(drag, c, null))}
                </tbody>
              </table>
            )}
          </SortableList>
        </div>
        {outside.rooms && <OutsideTable rooms={outside.rooms} onArchive={(id, on) => archive(id, on)} />}
      </div></div>
      <ChannelDialogs rooms={rooms} outside={outside.rooms ?? []} asked={asked} reload={reloadAll} />
    </section>
  );
}

/** "Private channels you are not in" (a `team` holder's, 2026-10-07): name, maker, people and last activity, never a message */
function OutsideTable({ rooms, onArchive }: { rooms: ChatOutsideRoom[]; onArchive: (id: string, on: boolean) => void }) {
  const { workspace } = useSession();
  const setParam = useSetParam();
  return (
    <>
      <h2 className="ob-manage__heading">Private channels you are not in</h2>
      {rooms.length === 0 ? <p className="ob-manage__intro">You are in every private channel.</p> : (
        <div className="ob-dtable-card">
          <table className="ob-dtable ob-dtable--manage" aria-label="Private channels you are not in">
            <thead><tr><th>Name</th><th className="ob-dtable__col--phone">Made by</th><th className="ob-dtable__center">Members</th><th className="ob-dtable__actions"><span className="ob-sr-only">Actions</span></th></tr></thead>
            <tbody>
              {rooms.map((r) => {
                const by = r.createdBy ? `Made by ${nameOf(workspace.members, r.createdBy)}` : "Workspace channel";
                const items: MenuItem[] = [r.archivedAt ? { id: "restore", label: "Restore", icon: "refresh", onSelect: () => onArchive(r.id, false) } : { id: "archive", label: "Archive", icon: "archive", onSelect: () => onArchive(r.id, true) },
                  { id: "delete", label: "Delete…", icon: "trash", onSelect: () => setParam("dialog", `delete-${r.id}`, true) }];
                return (
                  <tr key={r.id} className={r.archivedAt ? "ob-dtable__row--dim" : undefined}>
                    <td className="ob-dtable__grow"><span className="ob-dtable__name" title={`#${r.name}`}>#{r.name}</span> <Pill>Private</Pill>{r.archivedAt && <> <Pill>Archived</Pill></>}{r.lastEventAt && <span className="ob-dtable__desc">Active {formatRelative(r.lastEventAt)}</span>}</td>
                    <td className="ob-dtable__col--phone ob-dtable__muted">{by}{" "}</td>
                    <td className="ob-dtable__center">{" "}{r.memberCount}{" "}</td>
                    <td className="ob-dtable__actions"><span className="ob-dtable__row-actions">
                      {!r.archivedAt && <Button size="sm" onClick={() => setParam("dialog", `edit-${r.id}`, true)}>Edit</Button>}
                      <Menu align="end" trigger={<Button variant="icon" size="sm" icon="more" aria-label={`More for #${r.name}`} />} sections={[{ items }]} />
                    </span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** the screen's dialogs, their open state in the URL; Escape or Cancel clears it */
function ChannelDialogs({ rooms, outside, asked, reload }: { rooms: ChatRoom[]; outside: ChatOutsideRoom[]; asked: string | null; reload: () => Promise<void> }) {
  const setParam = useSetParam();
  const close = () => setParam("dialog", null);
  const id = (prefix: string) => (asked?.startsWith(prefix) ? asked.slice(prefix.length) : null);
  const inside = (x: string | null) => (x ? rooms.find((r) => r.kind === "channel" && r.id === x) : undefined);
  const outer = (x: string | null) => (x ? outside.find((r) => r.id === x) : undefined);
  const edit = id("edit-"), del = id("delete-");
  const editing = inside(edit), editingOutside = outer(edit);
  const deleting = inside(del) ?? outer(del);
  return (
    <>
      {asked === "new-channel" && <NewChannelDialog rooms={rooms} onClose={close} onCreated={() => { close(); void reload(); }} />}
      {editing && editing.slug !== "general" && <EditChannelDialog key={editing.id} channel={editing} rooms={rooms} onClose={close} reload={reload} />}
      {!editing && editingOutside && <EditOutsideDialog key={editingOutside.id} room={editingOutside} rooms={rooms} onClose={close} reload={reload} />}
      {deleting && <DeleteChannelDialog id={deleting.id} name={"slug" in deleting ? deleting.slug ?? "" : deleting.name} onClose={close} onDeleted={reload} />}
    </>
  );
}
